import React, { useState, useEffect, useContext } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../utils/api";
import DataTable from "../components/DataTable";

const TransactionsLedger = () => {
  const { userId } = useParams();
  const { user, formatCurrency, formatDate } = useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [clientUser, setClientUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isAdmin = user?.role === "admin";
  const isAdminViewingUser = isAdmin && userId;
  const isAdminViewingAll = isAdmin && !userId;
  const isUserViewingSelf = !isAdmin;

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        let response;

        if (isAdminViewingUser) {
          // Admin viewing specific user's transactions
          response = await api.get(`/transaction/admin/users/${userId}/ledger`);
          setClientUser(response.data.user);
        } else if (isAdminViewingAll) {
          // Admin viewing all transactions
          response = await api.get("/transaction/admin/ledger");
        } else {
          // User viewing their own transactions
          response = await api.get("/transaction/ledger");
        }
        // console.log("Transaction data:", response.data);
        if (response.data.success) {
          setTransactions(response.data.transactions);
        } else {
          throw new Error("Failed to fetch transactions");
        }
      } catch (err) {
        console.error("Error fetching transactions:", err);
        setError("Failed to load transaction data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [userId, isAdminViewingUser, isAdminViewingAll, isUserViewingSelf]);

  // Define columns based on the user role
  const getColumns = () => {
    const baseColumns = [
      {
        key: "date",
        header: "Date",
        render: (row) => formatDate(row.date),
      },
      //   {
      //     key: "transactionId",
      //     header: "Transaction ID",
      //   },
      {
        key: "type",
        header: "Type",
        render: (row) => (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              row.type === "deposit"
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {row.type === "deposit" ? "Deposit" : "Withdrawal"}
          </span>
        ),
      },
      {
        key: "amount",
        header: "Amount",
        render: (row) => (
          <span
            className={
              row.type === "deposit" ? "text-green-600" : "text-red-600"
            }
          >
            {row.type === "deposit" ? "+" : "-"} {formatCurrency(row.amount)}
          </span>
        ),
      },
      {
        key: "fdNumber",
        header: "FD Number",
      },
      {
        key: "actions",
        header: "Actions",
        render: (row) => {
          const fdLink = isAdmin
            ? `/admin/clients/${row.userId}/investments/${row.fdNumber}`
            : `/investments/${row.fdNumber}`;

          return (
            <Link
              to={fdLink}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              View FD Details
            </Link>
          );
        },
      },
    ];

    // Add user information columns for admin viewing all transactions
    if (isAdminViewingAll) {
      return [
        {
          key: "userDetails",
          header: "Client",
          render: (row) => (
            <Link
              to={`/admin/clients/${row.userId}/transactions-ledger`}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {row.userDetails?.name || "Unknown"} ({row.userId})
            </Link>
          ),
        },
        ...baseColumns,
        // {
        //   key: "processedBy",
        //   header: "Processed By",
        // },
      ];
    }

    // Add processed by column for admin viewing a specific user
    if (isAdminViewingUser) {
      return [
        ...baseColumns,
        // {
        //   key: "processedBy",
        //   header: "Processed By",
        // },
      ];
    }

    // Regular user view
    return baseColumns;
  };

  if (loading) {
    return (
      <div className="mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Transaction History</h1>
        <div className="flex items-center justify-center py-10">
          <div className="text-xl font-semibold">Loading transactions...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Transaction History</h1>
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-4 flex-wrap">
        <h1 className="text-2xl font-bold mb-1">
          {isAdminViewingAll
            ? "All Transactions"
            : isAdminViewingUser
            ? `${clientUser?.name}'s Transactions`
            : "My Transaction History"}
        </h1>

        {isAdmin && (
          <div className="flex gap-2">
            {isAdminViewingUser && (
              <Link
                to={`/admin/clients/${userId}/investments`}
                className="btn btn-outline btn-sm"
              >
                View User's Investments
              </Link>
            )}
          </div>
        )}
      </div>

      {isAdminViewingUser && clientUser && (
        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <h2 className="text-lg font-semibold mb-2">Client Information</h2>
          <div className="card main-content flex flex-col">
            <span className="text-sm text-gray-600">
              <strong>Name:</strong> {clientUser.name}
            </span>
            <span className="text-sm text-gray-600">
              <strong> User ID:</strong> {clientUser.userId}
            </span>
            <span className="text-sm text-gray-600">
              <strong>Email:</strong> {clientUser.email}
            </span>
          </div>
        </div>
      )}

      <div className="card main-content">
        <DataTable
          columns={getColumns()}
          data={transactions}
          emptyMessage="No transaction records found"
        />
      </div>
    </div>
  );
};

export default TransactionsLedger;
